import { useState, useEffect } from "react";
import { Account } from "@/types/Account";
import PageHeader from "@/components/molecules/PageHeader";
import Container from "@/components/atoms/Container";
import axios from "axios";
import Select from "@/components/molecules/Select";
import { router } from "@inertiajs/react";
import useDashboardAccountOptionStore from "@/store/dashboardAccountOption";
import useAccess from "@/hooks/useAccess";
import TicketNumbersChart from "@/components/organisms/Chart/TicketNumbersChart";
import TeamWorkloadChart from "@/components/organisms/Chart/TeamWorkloadChart";
import TicketStatusComponent from "@/components/organisms/Chart/TicketStatusComponent";
import TicketTypeChart from "@/components/organisms/Chart/TicketTypeChart";
import LoggedHoursChart from "@/components/organisms/Chart/LoggedHoursChart";
import WatchedTicketsTable from "@/components/organisms/Chart/WatchedTicketsTable";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/Accordion";
import TicketTable from "@/components/molecules/TicketTable";

type Props = {
  accounts: Account[];
};

type DashboardData = {
  active_tickets_count: number;
  total_tickets_count: number;
  team_members_count: number;
  watched_tickets_count: number;
  monthly_tickets: {
    month: string;
    created_ticket_count: number;
    closed_ticket_count: number;
  }[];
  team_tickets: {
    member_name: string;
    ticket_count: number;
  }[];
  ticket_statuses: {
    status_name: string;
    count: number;
  }[];
  ticket_types: {
    type_name: string;
    count: number;
  }[];
};

const Dashboard = ({ accounts }: Props) => {

  const breadcrumbItems = [{ label: "Dashboard", onClick: () => router.get("/"), }];
  const [dashBoardData, setDashBoardData] = useState<DashboardData>({
    active_tickets_count: 0,
    total_tickets_count: 0,
    team_members_count: 0,
    watched_tickets_count: 0,
    monthly_tickets: [],
    team_tickets: [],
    ticket_statuses: [],
    ticket_types: [],
  });
  const { selectedAccountId, setSelectedAccountId } = useDashboardAccountOptionStore();
  const [loggedHours, setLoggedHours] = useState(null);
  
  const { currentUser } = useAccess();

  useEffect(() => {
    if (
      currentUser &&
      selectedAccountId &&
      (!currentUser?.accounts?.some((account) => account.id.toString() === selectedAccountId)) &&
      (!currentUser?.roles?.some((role) => role.has_all_accounts_access))
    ) {
      setSelectedAccountId("");
    }

    if (
      currentUser &&
      (currentUser?.accounts?.length === 1)
    ) {
      setSelectedAccountId(currentUser?.accounts[0].id.toString());
    }

    const fetchData = async () => {
      if (selectedAccountId) {
        const response = await axios.get(`/dashboard_data/${selectedAccountId}`);
        setDashBoardData(response.data);
        setLoggedHours(response.data.today_log_duration_hours);
      }
    };

    fetchData();

  }, [selectedAccountId, currentUser, setSelectedAccountId]);

  async function handleAccountChange(value: string) {
    setSelectedAccountId(value);
  }

  return (
    <>
      <Container>
        <PageHeader title="Dashboard" breadcrumbItems={breadcrumbItems} />
        {currentUser && !currentUser?.roles?.some((role) => role.name === "client") && (
          <div className="flex flex-wrap items-center justify-between gap-4  pb-4">
            <div className="w-full md:w-auto">
              <Select
                options={
                  accounts.map((account) => ({
                    label: account.name,
                    value: account.id.toString(),
                  })) || []
                }
                value={selectedAccountId}
                onChange={(value) => handleAccountChange(value)}
                className='w-96 md:w-96'
                enabledSearch={accounts.length > 10}
              />
            </div>
            <div className="w-full md:w-auto">
              {loggedHours != null && (
                <div>
                  <LoggedHoursChart loggedHours={loggedHours} />
                </div>
              )}
            </div>
          </div>
        )}
      </Container>

      {selectedAccountId && dashBoardData && (
        <Container>
          <div className="p-4 space-y-6">
            <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-${currentUser && !currentUser?.roles?.some((role) => role.name === "client") ? '3' : '2'}`}>
              <div className="flex flex-col justify-between p-4 border dark:border-border rounded-md shadow-sm">
                <h3 className="text-sm font-semibold">Number of Tickets</h3>
                <p className="text-xl font-semibold text-right mt-2">{dashBoardData.total_tickets_count}</p>
              </div>
              {currentUser && !currentUser?.roles?.some((role) => role.name === "client") && (
                <div className="flex flex-col justify-between p-4 border dark:border-border rounded-md  shadow-sm">
                  <h3 className="text-sm font-semibold">Number of Team Members</h3>
                  <p className="text-xl font-semibold text-right mt-2">{dashBoardData.team_members_count}</p>
                </div>
              )}
              <div className="flex flex-col justify-between p-4 border dark:border-border rounded-md  shadow-sm">
                <h3 className="text-sm font-semibold">Watched Tickets</h3>
                <p className="text-xl font-semibold text-right mt-2">{dashBoardData.watched_tickets_count}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className={`grid grid-cols-1 md:grid-cols-${currentUser && !currentUser?.roles?.some((role) => role.name === "client") ? '3' : '2'} gap-4`}>
                <div>
                  <TicketNumbersChart monthly_tickets={dashBoardData.monthly_tickets} />
                </div>
                {currentUser && !currentUser?.roles?.some((role) => role.name === "client") && (
                  <div>
                    <TeamWorkloadChart team_tickets={dashBoardData.team_tickets} />
                  </div>
                )}
                <div>
                  <WatchedTicketsTable accountId={selectedAccountId} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <TicketStatusComponent ticket_statuses={dashBoardData.ticket_statuses} />
                </div>
                <div>
                  <TicketTypeChart ticket_types={dashBoardData.ticket_types} />
                </div>
              </div>
            </div>

            <Accordion
              className="w-full rounded-md"
              type="single"
              defaultValue="openTickets"
              collapsible
            >
              <AccordionItem value="openTickets" className="shadow px-5 mb-5 border border-border rounded">
                <div className="relative w-full">
                  <AccordionTrigger className="text-md font-semibold">OPEN Tickets</AccordionTrigger>
                  <AccordionContent className="w-full px-4">
                    <TicketTable account_id={selectedAccountId} ticket_status_id="1" />
                  </AccordionContent>
                </div>
              </AccordionItem>

              <AccordionItem value="newClientReplyTickets" className="shadow px-5 mb-5 border border-border rounded">
                <div className="relative w-full">
                  <AccordionTrigger className="text-md font-semibold">NEW CLIENT REPLY Tickets</AccordionTrigger>
                  <AccordionContent className="w-full px-4">
                    <TicketTable account_id={selectedAccountId} ticket_status_id="4" />
                  </AccordionContent>
                </div>
              </AccordionItem>

              <AccordionItem value="onHoldTickets" className="shadow px-5 mb-5 border border-border rounded">
                <div className="relative w-full">
                  <AccordionTrigger className="text-md font-semibold">ON HOLD Tickets</AccordionTrigger>
                  <AccordionContent className="w-full px-4">
                    <TicketTable account_id={selectedAccountId} ticket_status_id="7" />
                  </AccordionContent>
                </div>
              </AccordionItem>
            </Accordion>
          </div>
        </Container>
      )}
    </>
  );
};

export default Dashboard;
